// Shared button used for workflow submit action.
import CreateButton from "global/components/CreateButton";

// Shared image upload tile used for workflow image inputs.
import ImageUploadTile from "global/components/ImageUploadTile";

// Shared image uploader used here in button mode so AI Studio uses the same bucket picker as Designs.
import RenderImageUploader from "global/components/ImageUploader";



// Render and submit the selected workflow form.
const AiWorkflowForm = ({
  selectedWorkflow,
  selectedWorkflowImageField,
  selectedWorkflowFormFields,
  workflowFormValues,
  workflowUploadingFields,
  isWorkflowImageField,
  isWorkflowPromptField,
  handleWorkflowImageFileSelected,
  handleWorkflowFieldChange,
  clearSelectedWorkflowForm,
  handleGenerateWorkflow,
  authReady,
  isAnyWorkflowUploadInProgress,
}) => {

  // Render one workflow setting field based on its type.
  const renderWorkflowSettingField = (field, className = "") => {

    // Read the current value from workflow form state.
    const value = workflowFormValues[field.setting];

    return (

      <div
        key={field.setting}
        className={`ai-workflow-field ${className}`.trim()}
      >

        <label htmlFor={`workflow-setting-${field.setting}`}>{field.name}</label>

        {/* Image setting uses upload + bucket picker controls. */}
        {isWorkflowImageField(field) ? (

          <div className="ai-workflow-image-upload-row">

            <ImageUploadTile
              value={value}
              alt={field.name}
              uploading={Boolean(workflowUploadingFields[field.setting])}
              onFileSelected={(file) => handleWorkflowImageFileSelected(field.setting, file)}
              maxWidth="100%"
              fromBucketButton={
                <RenderImageUploader
                  selectedImage={value}
                  displayMode="button"
                  buttonTitle="From Bucket"
                  buttonSize="s"
                  bucketSelectionMode="single"
                  onImageChange={(selectedImage) => {

                    let selectedImageUrl = "";

                    if (selectedImage && selectedImage.imageBucketUrl) {

                      selectedImageUrl = selectedImage.imageBucketUrl;

                    } else if (
                      selectedImage &&
                      selectedImage.imageDetails &&
                      selectedImage.imageDetails.image_url
                    ) {

                      selectedImageUrl = selectedImage.imageDetails.image_url;

                    }

                    if (!selectedImageUrl) { return; }

                    handleWorkflowFieldChange(field.setting, selectedImageUrl);

                  }}
                />
              }
            />

          </div>

        ) : field.type === "option" ? (

          <select
            id={`workflow-setting-${field.setting}`}
            value={value ?? ""}
            onChange={(e) => handleWorkflowFieldChange(field.setting, e.target.value)}
          >

            {(field.options || []).map((option) => (

              <option key={option.code} value={option.code}>

                {option.name}

              </option>

            ))}

          </select>

        ) : (field.type === "int" || field.type === "float") ? (

          <input
            id={`workflow-setting-${field.setting}`}
            type="number"
            value={value ?? ""}
            onChange={(e) => handleWorkflowFieldChange(field.setting, e.target.value)}
          />

        ) : (field.type === "bool" || field.type === "boolean") ? (

          <input
            id={`workflow-setting-${field.setting}`}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => handleWorkflowFieldChange(field.setting, e.target.checked)}
          />

        ) : (

          // Prompt-like fields use a textarea to support longer input.
          isWorkflowPromptField(field) ? (

            <textarea
              id={`workflow-setting-${field.setting}`}
              rows={2}
              value={value ?? ""}
              onChange={(e) => handleWorkflowFieldChange(field.setting, e.target.value)}
            />

          ) : (

            <input
              id={`workflow-setting-${field.setting}`}
              type="text"
              value={value ?? ""}
              onChange={(e) => handleWorkflowFieldChange(field.setting, e.target.value)}
            />

          )

        )}

        {field.description && <small>{field.description}</small>}

      </div>

    );

  };

  return (

    <div id="ai-workflow-form" className="ai-workflow-form">

      {/* Placeholder shown until a workflow is selected from the left panel. */}
      {!selectedWorkflow && (

        <p>Select a workflow to configure it.</p>

      )}

      {selectedWorkflow && (

        <div className="ai-workflow-form-content">

          {/* Workflow title/description and a quick reset action. */}
          <div className="ai-workflow-form-header">

            <div className="ai-workflow-form-title-row">

              <h3>{selectedWorkflow.name}</h3>

              <p className="ai-workflow-form-description">{selectedWorkflow.description}</p>

            </div>

            <button
              type="button"
              className="ai-workflow-clear-btn"
              onClick={clearSelectedWorkflowForm}
            >

              Clear Settings

            </button>

          </div>

          {/* Split layout: image input pane (if present) + remaining editable fields. */}
          <div className={`ai-workflow-layout ${selectedWorkflowImageField ? "ai-workflow-layout--with-image" : ""}`}>

            {selectedWorkflowImageField && (

              /* Primary image field gets its own larger pane for preview/upload interactions. */
              <div className="ai-workflow-image-pane">

                {renderWorkflowSettingField(selectedWorkflowImageField, "ai-workflow-field--image-pane")}

              </div>

            )}

            <div className="ai-workflow-fields-pane">

              {/* Non-image editable fields rendered in grid form. */}
              <div className="ai-workflow-fields">

                {selectedWorkflowFormFields.map((field) => {

                  const fullWidthClass = (isWorkflowPromptField(field) || isWorkflowImageField(field))
                    ? "ai-workflow-field--full"
                    : "";

                  return renderWorkflowSettingField(field, fullWidthClass);

                })}

              </div>

              {/* Start Workflow - Submit action disabled during auth init or while an upload is in progress. */}
              <CreateButton
                title={`Run ${selectedWorkflow.name}`}
                onClick={handleGenerateWorkflow}
                disabled={!authReady || isAnyWorkflowUploadInProgress}
                className="ai-workflow-submit-btn"
                color="var(--primary-color, #00a43b)"
              />

            </div>

          </div>

        </div>
        
      )}

    </div>

  );

};

export default AiWorkflowForm;


